"use client"
import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import Script from 'next/script';
import { Container, Row, Col, ProgressBar, Badge, Tooltip, OverlayTrigger, Tab, Tabs, Alert, Button, Spinner } from 'react-bootstrap';
import * as Icon from 'react-feather';
import dynamic from 'next/dynamic';
const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });
const GaugeChart = dynamic(() => import('react-gauge-chart'), { ssr: false });

import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import Footer from '../../components/footer';
import Search from '../../components/search';
import FiyatGrafik from '../../components/FiyatGrafik';
import YatirimciSayisiGrafik from '../../components/YatirimciSayisiGrafik';
import KayipKazancGrafik from '../../components/KayipKazancGrafik';
import { getCookie } from '../../utils/localStorage';
import Mobile from '../../components/mobile';

export default function FonAnaliz({ data, getiriler, fiyatlar, yatirimciSayisi, kayipKazanc, portfoyDagilim, portfoyDetay, getiriKarsilastirma }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(false);
  const [favoriMi, setFavoriMi] = useState(false);
  const [favoriButton, setFavoriButton] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const textToRead = data.dinle;

  const handleToggleSpeech = () => {
    if (isSpeaking) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      const utterance = new SpeechSynthesisUtterance(textToRead);
      utterance.lang = 'tr-TR'; // Türkçe dil desteği için
      utterance.rate = 0.8;
      utterance.onend = () => setIsSpeaking(false);
      speechSynthesis.speak(utterance);
      setIsSpeaking(true);
    }
  };
  
  const [chartData, setChartData] = useState({
    series: [{
      name: 'Yıllık Getiri Yüzdesi',
      data: []
    }],
    options: {
      chart: {
        id: 'yillik-getiri',
        toolbar: {
          show: false  // Grafik üzerindeki araç çubuğunu kaldırır
        },
        zoom: {
          enabled: false  // Zoom özelliğini kapatır
        }
      },
      xaxis: {
        categories: [],
        labels: {
          show: true  // X ekseni etiketlerini gösterir
        },
        axisTicks: {
          show: false
        },
        axisBorder: {
          show: false
        }
      },
      yaxis: {
        labels: {
          show: false  // Y ekseni etiketlerini gösterir
        }
      },
      grid: {
        show: false  // Arka plandaki grid çizgilerini kaldırır
      },
      plotOptions: {
        bar: {
          borderRadius: 5,  // Sütunların köşelerini yuvarlar
          horizontal: false,
          columnWidth: '80%',  // Sütun genişliği, yüzde olarak
          endingShape: 'rounded'  // Sütunların üst kısmını yuvarlak yapar
        }
      },
      colors: ['#5151F9'],  // Tüm sütunlar için mavi renk
    }
  });

  const [getiriKarsilastirmaChartData, setGetiriKarsilastirmaChartData] = useState({
    series: [{
      name: 'Getiri Karşılaştırma',
      data: []
    }],
    options: {
      chart: {
        id: 'getiri-karsilastirma',
        toolbar: {
          show: false  // Grafik üzerindeki araç çubuğunu kaldırır
        },
        zoom: {
          enabled: false  // Zoom özelliğini kapatır
        }
      },
      colors: ['#5151F9', '#6C757D', '#11B981', '#0EA5E9', '#E11D48', '#4572A7', '#2A2C3E', '#FFBB2A'],
      xaxis: {
        categories: [],
        labels: {
          show: false  // X ekseni etiketlerini gösterir
        },
        axisTicks: {
          show: false
        },
        axisBorder: {
          show: false
        }
      },
      yaxis: {
        labels: {
          show: false  // Y ekseni etiketlerini gösterir
        }
      },
      grid: {
        show: false  // Arka plandaki grid çizgilerini kaldırır
      },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 5,  // Sütunların köşelerini yuvarlar
          horizontal: false,
          columnWidth: '80%',  // Sütun genişliği, yüzde olarak
          endingShape: 'rounded'  // Sütunların üst kısmını yuvarlak yapar
        }
      },
    }
  });

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  }

  const renderTooltip = (props, tooltipText) => (
    <Tooltip id="button-tooltip" {...props}>
      {tooltipText}
    </Tooltip>
  );

  const pozitifGetiriGunOraniOptions = {
    chart: {
      type: 'radialBar',
      offsetY: 0
    },
    plotOptions: {
      radialBar: {
        startAngle: -180,
        endAngle: 180,
        hollow: {
          margin: 0,
          size: '70%',
          background: 'transparent',
          image: undefined,
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            show: false,
          }
        },
        track: {
          background: '#DEE2E6',
          strokeWidth: '100%',
          margin: 5,
        }
      }
    },
    fill: {
      type: 'solid',
      colors: ['#11B981']
    },
    stroke: {
      lineCap: 'round'
    },
    labels: ['Progress'],
  };

  const negatifGetiriGunOraniOptions = {
    chart: {
      type: 'radialBar',
      offsetY: 0
    },
    plotOptions: {
      radialBar: {
        startAngle: -180,
        endAngle: 180,
        hollow: {
          margin: 0,
          size: '70%',
          background: 'transparent',
          image: undefined,
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            show: false,
          }
        },
        track: {
          background: '#DEE2E6',
          strokeWidth: '100%',
          margin: 5,
        }
      }
    },
    fill: {
      type: 'solid',
      colors: ['#E11D48']
    },
    stroke: {
      lineCap: 'round'
    },
    labels: ['Progress'],
  };

  useEffect(() => {
    const yillar = getiriler.map(item => item.yil);
    const getiriYuzdeleri = getiriler.map(item => parseFloat(item.yillik_getiri_yuzdesi));

    setChartData(prevState => ({
      ...prevState,
      series: [{ ...prevState.series[0], data: getiriYuzdeleri }],
      options: {
        ...prevState.options,
        xaxis: { ...prevState.options.xaxis, categories: yillar }
      }
    }));

    const arac = getiriKarsilastirma.map(item => item.arac);
    const yuzdeler = getiriKarsilastirma.map(item => parseFloat(item.yuzde));
    
    setGetiriKarsilastirmaChartData(prevState => ({
      ...prevState,
      series: [{ ...prevState.series[0], data: yuzdeler }],
      options: {
        ...prevState.options,
        xaxis: { ...prevState.options.xaxis, categories: arac }
      }
    }));
  }, []);

  function ayVer(ayNumarasi) {
    const aylar = [
      'OCA', 'ŞUB', 'MAR', 'NİS', 'MAY', 'HAZ',
      'TEM', 'AĞU', 'EYL', 'EKİ', 'KAS', 'ARA'
    ];
  
    // Ay numarası 1-12 arasında olmalıdır, ancak diziler 0'dan başlar.
    if (ayNumarasi < 1 || ayNumarasi > 12) {
      return 'Geçersiz ay numarası'; // Geçersiz girişler için hata mesajı döndür
    }
  
    // Dizi indeksi 0'dan başladığı için 1 çıkarıyoruz.
    return aylar[ayNumarasi - 1];
  }

  const portfoyDetayLabels = portfoyDetay.map(item => item.arac);
  const portfoyDetaySeries = portfoyDetay.map(item => parseFloat(item.yuzde));

  // Grafik ayarları
  const portfoyDetayOptions = {
    chart: {
      type: 'donut'
    },
    labels: portfoyDetayLabels,
    colors: ['#5151F9', '#11B981', '#0EA5E9', '#FFBB2A', '#E11D48', '#6C757D'],
    legend: {
      position: 'bottom',
      offsetY: 0,
      height: 0,
    }
  };

  useEffect(() => {
    const isLogin = getCookie('isLogin');

    if (isLogin) {
        fetchUserData();
    }
  }, []);

  const fetchUserData = async () => {
    const uid     = getCookie('uid');
    const eposta  = getCookie('eposta');
    const auth    = getCookie('auth');
    try {
        const response = await fetch(`https://api.yatirimnedir.com/giris-kontrol.php?uid=${uid}&eposta=${eposta}&auth=${auth}`);
        const data = await response.json();
        if(data.status) {
          setUserData(true);
          isFovorite();
        }
    } catch (error) {
        console.error('API isteğinde hata oluştu:', error);
    }
  };

  const isFovorite = async() => {
    const uid     = getCookie('uid');
    const eposta  = getCookie('eposta');
    const auth    = getCookie('auth');
    const kod     = data.id;

    try {
      const response = await fetch(`https://api.yatirimnedir.com/favori-mi.php?uid=${uid}&eposta=${eposta}&auth=${auth}&kod=${kod}`);
      const data = await response.json();
      if(data.status) {
        setFavoriMi(true);
      }
    } catch (error) {
      setFavoriMi(false);
    }
  };

  const handleFavorite = async() => {
    const uid     = getCookie('uid');
    const eposta  = getCookie('eposta');
    const auth    = getCookie('auth');
    const kod     = data.id;

    try {
      if(favoriMi) {
        const response = await fetch(`https://api.yatirimnedir.com/favoriden-cikar.php?uid=${uid}&eposta=${eposta}&auth=${auth}&kod=${kod}`);
        const data = await response.json();
        if(data.status) {
          setFavoriMi(false);
        }
      }else{
        const response = await fetch(`https://api.yatirimnedir.com/favoriye-ekle.php?uid=${uid}&eposta=${eposta}&auth=${auth}&kod=${kod}&tip=fon`);
        const data = await response.json();
        if(data.status) {
          setFavoriMi(true);
        }
      }
    } catch (error) {
      isFovorite();
    }
  };

  return (
    <>
      <Head>
        <title>{data.kod} - {data.fon_adi} Fon Analiz - Yatırım Nedir</title>
        <meta name="description" content={`${data.kod} - ${data.fon_adi_capitalized_case} detaylı analizi. Son 1 yıllık pozitif getirili gün sayısı: ${data.pozitifGetiriliGunSayisi} GÜN - Kategori: ${data.kategori} - Son Fiyat: ${data.fiyatTL}`} key="desc" />
        <meta name="robots" content="index, follow" />
        <meta name="publisher" content="Yatırım Nedir" />
        <link rel="canonical" href={`https://www.yatirimnedir.com/fon-analiz/${data.seflink}`} />
        <meta property="og:site_name" content="Yatırım Nedir?" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${data.kod} - ${data.fon_adi} Fon Analiz - Yatırım Nedir`} />
        <meta property="og:description" content={`${data.kod} - ${data.fon_adi_capitalized_case} detaylı analizi. Son 1 yıllık pozitif getirili gün sayısı: ${data.pozitifGetiriliGunSayisi} GÜN - Kategori: ${data.kategori} - Son Fiyat: ${data.fiyatTL}`} />
        <meta property="og:url" content={`https://www.yatirimnedir.com/fon-analiz/${data.seflink}`} />
        <meta property="og:image" content={`https://api.yatirimnedir.com/image.php?isim=${data.kod} - ${encodeURIComponent(data.fon_adi)}`} />
        <meta property="og:image:secure_url" content={`https://api.yatirimnedir.com/image.php?isim=${data.kod} - ${encodeURIComponent(data.fon_adi)}`} />
        <meta property="og:image:alt" content={`${data.kod} - ${data.fon_adi} Fon Analiz - Yatırım Nedir`} />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${data.kod} - ${data.fon_adi} Fon Analiz - Yatırım Nedir`} />
        <meta name="twitter:description" content={`${data.kod} - ${data.fon_adi_capitalized_case} detaylı analizi. Son 1 yıllık pozitif getirili gün sayısı: ${data.pozitifGetiriliGunSayisi} GÜN - Kategori: ${data.kategori} - Son Fiyat: ${data.fiyatTL}`} />
        <meta name="twitter:site" content="@https://twitter.com/YatirimNedir" />
        <meta name="twitter:creator" content="@https://twitter.com/YatirimNedir" />
        <meta name="twitter:image" content={`https://api.yatirimnedir.com/image.php?isim=${data.kod} - ${encodeURIComponent(data.fon_adi)}`} />
        <meta name="google-adsense-account" content="ca-pub-8157147392800140" />
      </Head>
      <Script
        async="true"
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8157147392800140"
        strategy="afterInteractive"
        crossorigin="anonymous"
      />
      <div className="app-root d-flex flex-column">
        <Sidebar 
          isOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
        />

        <main className='app-main'>
          <Header
            isOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
          />
          
          <div className='app-content pt-3 pt-lg-4 px-0 px-lg-2'>
            <Container>
              <Mobile />
              <Row className='mb-4'>
                <Col>
                  <div className='bg-sidebar rounded p-4'>
                    <div className='page-title-container'>
                      <div className='page-title'>
                        <h1 className='text-white d-flex align-items-center h5 mb-2'>
                          <div className="text-decoration-none text-heading d-flex align-items-center">
                            <span className='item-icon me-3'>{data.kod}</span>
                          </div>
                          {data.fon_adi}
                        </h1>
                        <nav className="breadcrumb-nav" aria-label="breadcrumb">
                          <ol className="breadcrumb">
                          <li className="breadcrumb-item"><Link href="/" prefetch={false}>Anasayfa</Link></li>
                          <li className="breadcrumb-item"><Link href="/fon-filtreleme" prefetch={false}>Fon Filtreleme</Link></li>
                            <li className="breadcrumb-item active" aria-current="page">{data.kod} - {data.fon_adi_capitalized_case}</li>
                          </ol>
                        </nav>
                      </div>
                      <div className='p-3'>
                        {data.sponsorMobil &&
                          <Button variant="primary" size='sm' className='w-100 mb-2 d-block d-lg-none' href={data.sponsorMobil} target="_blank">Bu Fonu Midas’ta İncele</Button>
                        }
                        {data.sponsorWeb &&
                          <Button variant="primary" size='sm' className='w-100 mb-2 d-none d-lg-block d-xl-block' href={data.sponsorWeb} target="_blank">Midas’ı İncele</Button>
                        }
                        {userData &&
                          <>
                            <Button variant="danger" size='sm' className='w-100' onClick={handleFavorite} disabled={!favoriButton}>
                              {favoriButton ? 
                                (favoriMi ? 'Favoriden Çıkar' : 'Favoriye Ekle') : 
                                <Spinner animation="border" role="status" size="sm" />
                              }
                            </Button>
                          </>
                        }

                        {!userData &&
                          <Button variant="danger" size='sm' className='w-100' href="/giris-yap">Favoriye Ekle</Button>
                        }
                      </div>
                    </div>
                    <Row>
                      <Col lg={12}>
                        <Search />
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Row className='mb-4 text-center'>
                <Col>
                  <Link prefetch={false} href='https://app.getmidas.com/gmih/ti6lsyc6' className='d-none d-lg-block d-xl-block'>
                    <Image src='https://api.yatirimnedir.com/images/midas/web/fon.png' target="_blank" width='1300' height='250' className='img-fluid rounded' quality={75} />
                  </Link>
                  <Link prefetch={false} href='https://app.getmidas.com/gmih/ti6lsyc6' target="_blank" className='d-block d-lg-none'>
                    <Image src='https://api.yatirimnedir.com/images/midas/mobile/fon.png' width='300' height='250' className='img-fluid rounded' quality={75} />
                  </Link>
                </Col>
              </Row>

              <Row className='mb-4'>
                <Col className='text-center'>
                  <Button variant='primary' className='w-100' onClick={handleToggleSpeech}>
                    {isSpeaking ? 'Durdur' : 'Bu Fonu Dinle'}
                  </Button>
                </Col>
              </Row>

              <Row className='mb-4'>
                <Col>
                  <div className='card-shadow p-4'>
                    <h2 className='h6 text-center mb-3'>Doluluk Oranı</h2>
                    <ProgressBar variant="primary" now={data.doluluk_orani} label={`%${data.doluluk_orani}`} className='mb-2' />
                    <div className='d-flex font-small justify-content-between'>
                      <div>
                        <span>TEDAVÜLDEKİ PAY: </span>
                        <strong className='text-heading'>{data.tedavuldeki_pay}</strong>
                      </div>
                      <div>
                        <span>TOPLAM PAY: </span>
                        <strong className='text-heading'>{data.toplam_pay === '0' ? 'Bilinmiyor' : data.toplam_pay}</strong>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xl={5} lg={12} className='mb-4'>
                  <div className='card-shadow'>
                    <h2 className='h6 m-0 px-4 pt-4 pb-2'>Genel Bilgiler</h2>
                    <hr/>
                    <div className='p-4 pt-0'>
                      <table className='table table-border-dashed font-small m-0'>
                        <tbody>
                          <tr>
                            <td className='text-color'>Fon Kodu</td>
                            <td><strong>{data.kod}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Fon Adı</td>
                            <td><strong>{data.fon_adi_capitalized_case}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Fon Kurucusu</td>
                            <td><strong>{data.sirket}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Fon Kategorisi</td>
                            <td><strong>{data.kategori}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Yönetim Ücreti</td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <strong className='me-2'>{data.yonetim_ucreti}</strong>
                                <Badge pill bg={data.yonetim_ucreti_badge_renk}>{data.yonetim_ucreti_badge}</Badge>
                              </div>  
                            </td>
                          </tr>
                          <tr>
                            <td className='text-color'>Azami Gider</td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <strong className='me-1'>{data.azami_gider}</strong>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td className='text-color'>Vergilendirme</td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <strong className='me-1'>{data.stopaj}</strong>
                                {data.stopaj !== 'Vergiden Muaf' &&
                                  <span>😢</span>
                                }
                                {data.stopaj === 'Vergiden Muaf' &&
                                  <span>😍</span>
                                }
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td className='text-color'>Toplam Değer</td>
                            <td><strong>{data.toplam_deger}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Yatırımcı Sayısı</td>
                            <td><strong>{data.yatirimci_sayisi}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Pazar Payı</td>
                            <td><strong>{data.pazar_payi}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>İşlem Başlangıç/Bitiş Saati</td>
                            <td><strong>{data.saat}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Fiyat TL</td>
                            <td><strong className='text-primary font-normal'>{data.fiyatTL}</strong></td>
                          </tr>
                          <tr>
                            <td className='text-color'>Fiyat USD</td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <strong className='text-primary font-normal'>{data.fiyatUSD}</strong> 
                                <OverlayTrigger
                                  placement="right"
                                  overlay={(props) => renderTooltip(props, 'Bu fiyat merkez bankasının bir iş günü önce açıkladığı (Efektif Satış) kuruyla hesaplanmıştır!')} // renderTooltip fonksiyonuna tooltip metnini gönder
                                >
                                  <Icon.Info className='ms-2' size={12}/>
                                </OverlayTrigger>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Col>
                <Col xl={4} lg={12}>
                  <div className='card-shadow p-4 mb-4'>
                    <h2 className='h6 text-center m-0'>Son 1 Yıllık Pozitif - Negatif Gün Getisi</h2>
                    <Row>
                      {data.getiri_1y === '0.00' ?
                        <Col lg={12} className='text-center mt-4'>
                          <Alert variant='warning'>
                            Bu fon 1 yaşından küçük olduğu için hesaplanamıyor.
                          </Alert>
                        </Col> : 
                        (
                          <>
                            <Col lg={6} className='text-center'>
                              <div className='radial-chart'>
                                <div className='chart-content'>
                                  <span className='emoji'>😊</span>
                                  <h3 className='h6'>{data.pozitifGetiriliGunSayisi} GÜN</h3>
                                  <p>%{data.pozitifGetiriGunOrani}</p>
                                </div>
                                <Chart options={pozitifGetiriGunOraniOptions} series={[data.pozitifGetiriGunOrani]} type="radialBar" height={240} />
                              </div>
                              <small>POZİTİF GETİRİLİ GÜN SAYISI</small>
                            </Col>
                            <Col lg={6} className='text-center'>
                              <div className='radial-chart'>
                                <div className='chart-content'>
                                  <span className='emoji'>😞</span>
                                  <h3 className='h6'>{data.negatifGetiriliGunSayisi} GÜN</h3>
                                  <p>%{data.negatifGetiriGunOrani}</p>
                                </div>
                                <Chart options={negatifGetiriGunOraniOptions} series={[data.negatifGetiriGunOrani]} type="radialBar" height={240} />
                              </div> 
                              <small>NEGATİF GETİRİLİ GÜN SAYISI</small>
                            </Col>
                          </>
                        )
                      }
                    </Row>
                  </div>

                  <div className='card-shadow p-3 pb-0 mb-4'>
                    <GaugeChart
                      id="gauge-chart3"
                      nrOfLevels={7}
                      colors={["#11B981", "#E11D48"]}
                      arcWidth={0.1}
                      percent={data.risk / 7.5}
                      textColor={'black'}
                      formatTextValue={(value) => `Risk ${data.risk}`}
                      hideText={false}
                    />
                  </div>
                  <Row>
                    <Col lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-success rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>Alış Valörü</h2>
                          <OverlayTrigger
                            placement="right"
                            overlay={(props) => renderTooltip(props, `Alış emri verdiğiniz zaman ${data.alis_valor} gün içinde alım gerçekleşip hesabınıza yansır.`)}
                          >
                            <Icon.Info className='ms-2' size={12}/>
                          </OverlayTrigger>
                        </div>
                        <div className='card-shadow p-3 z-1 mt-n-10 text-center'>
                          <strong className='h1 d-block'>{data.alis_valor}</strong>
                          <small className='text-color'>GÜN İÇİNDE ALIŞ GERÇEKLEŞİR</small>
                        </div>
                      </div>
                    </Col>
                    <Col lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-danger rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>Satış Valörü</h2>
                          <OverlayTrigger
                            placement="right"
                            overlay={(props) => renderTooltip(props, `Satış emri verdiğiniz zaman ${data.satis_valor} gün içinde satış gerçekleşip hesabınıza yansır.`)}
                          >
                            <Icon.Info className='ms-2' size={12}/>
                          </OverlayTrigger>
                        </div>
                        <div className='card-shadow p-3 z-1 mt-n-10 text-center'>
                          <strong className='h1 d-block'>{data.satis_valor}</strong>
                          <small className='text-color'>GÜN İÇİNDE SATIŞ GERÇEKLEŞİR</small>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col xl={3} lg={12}>
                  <div className='card-shadow p-4 text-center mb-4'>
                    <h2 className='h6 mb-3'>Param Şimdi Ne Oldu?</h2>
                    {data.getiri_1y === '0.00' ?
                      <Alert variant='warning'>
                        Bu fon 1 yaşından küçük olduğu için hesaplanamıyor.
                      </Alert> : 
                      (
                        <>
                          <span className='d-block text-color font-small mb-2'>Bu fona 1 yıl önce </span>
                          <p className='mb-2'><strong className='text-heading'>100 ₺</strong> yatırsaydınız.</p>
                          <span className='d-block text-primary display-6 mb-2'>{data.tl100} ₺</span>
                          <p className='mb-2'><strong className='text-heading'>100 $</strong> yatırsaydınız.</p>
                          <span className='d-block text-primary display-6 mb-2'>{data.usd100} $</span>
                          <span className='d-block text-color font-small'>Olmuştu</span>
                      </>
                      )
                    }
                  </div>

                  <div className='card-shadow p-4 text-center mb-4'>
                    <div>
                      {data.ic_tuzuk && (
                        <Link prefetch={false} href={data.ic_tuzuk} className='border-dashed rounded d-flex align-items-center justify-content-start text-color text-decoration-none font-small text-heading mb-3'>
                          <div className='bg-light box-48 d-flex align-items-center justify-content-center text-color me-3'>
                            <Icon.File size={22} />
                          </div>
                          <span>Fon İçtüzüğü</span>
                        </Link>
                      )}

                      {data.izahname && (
                        <Link prefetch={false} href={data.izahname} className='border-dashed rounded d-flex align-items-center justify-content-start text-color text-decoration-none font-small text-heading mb-3'>
                          <div className='bg-light box-48 d-flex align-items-center justify-content-center text-color me-3'>
                            <Icon.File size={22} />
                          </div>
                          <span>Güncel İzahname</span>
                        </Link>
                      )}

                      {data.bilgi_formu && (
                        <Link prefetch={false} href={data.bilgi_formu} className='border-dashed rounded d-flex align-items-center justify-content-start text-color text-decoration-none font-small text-heading mb-3'>
                          <div className='bg-light box-48 d-flex align-items-center justify-content-center text-color me-3'>
                            <Icon.File size={22} />
                          </div>
                          <span>Yatırımcı Bilgi Formu</span>
                        </Link>
                      )}

                      <Link prefetch={false} href={`https://www.tefas.gov.tr/FonAnaliz.aspx?FonKod=${data.kod}`} target='_blank' className='border-dashed rounded d-flex align-items-center justify-content-start text-color text-decoration-none font-small text-heading mb-3'>
                        <div className='bg-light box-48 d-flex align-items-center justify-content-center text-color me-3'>
                          <Icon.ExternalLink size={22} />
                        </div>
                        <span>TEFAS Adresi</span>
                      </Link>

                      {data.kap_url && (
                        <Link prefetch={false} href={data.kap_url} target='_blank' className='border-dashed rounded d-flex align-items-center justify-content-start text-color text-decoration-none font-small text-heading mb-3'>
                          <div className='bg-light box-48 d-flex align-items-center justify-content-center text-color me-3'>
                            <Icon.ExternalLink size={22} />
                          </div>
                          <span>KAP Adresi</span>
                        </Link>
                      )}

                      {data.web_url && (
                        <Link prefetch={false} href={data.web_url} target='_blank' className='border-dashed rounded d-flex align-items-center justify-content-start text-color text-decoration-none font-small text-heading'>
                          <div className='bg-light box-48 d-flex align-items-center justify-content-center text-color me-3'>
                            <Icon.ExternalLink size={22} />
                          </div>
                          <span>Web Sitesi</span>
                        </Link>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              {data.makaleSayisi > 0 &&
                <Row className='mb-4'>
                  <Col lg={12}>
                    <div className='card-shadow blog-detay'>
                      <div className='p-4 d-flex flex-column gap-3'>
                        <h2 className='border-bottom-dashed pb-2 mb-0'>{data.kod} Fonu İle İlgili Aşağıdaki Makaleler İlginizi Çekebilir</h2>
                        {data.makale.map((item, index) => (
                          <Link prefetch={false} href={`/blog-detay/${item.seflink}`} className="text-decoration-none text-heading d-flex align-items-center border-bottom-dashed pb-3" title={item.baslik}>
                            <Badge className='me-3' pill>{item.tarih}</Badge>
                            <span>{item.baslik}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              }

              <Row className='mb-4'>
                <Col lg={12}>
                  <div className='card-shadow p-4 pb-0'>
                    <Row>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>GÜNLÜK</span>
                        <strong className={`d-block ${parseFloat(data.getiri_1g) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_1g}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>HAFTALIK</span>
                        <strong className={`d-block ${parseFloat(data.getiri_1h) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_1h}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>1 AY</span>
                        <strong className={`d-block ${parseFloat(data.getiri_1a) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_1a}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>3 AY</span>
                        <strong className={`d-block ${parseFloat(data.getiri_3a) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_3a}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>6 AY</span>
                        <strong className={`d-block ${parseFloat(data.getiri_6a) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_6a}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>YBB</span>
                        <strong className={`d-block ${parseFloat(data.getiri_yb) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_yb}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>1 YIL</span>
                        <strong className={`d-block ${parseFloat(data.getiri_1y) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_1y}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>3 YIL</span>
                        <strong className={`d-block ${parseFloat(data.getiri_3y) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_3y}
                        </strong>
                      </Col>
                      <Col className='mb-4'>
                        <span className='d-block font-small mb-3'>5 YIL</span>
                        <strong className={`d-block ${parseFloat(data.getiri_5y) < 0 ? 'text-danger' : 'text-success'}`}>
                          %{data.getiri_5y}
                        </strong>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={12} className='mb-4'>
                  <div className='card-shadow'>
                    <h2 className='h6 m-0 px-4 pt-4 pb-2'>Yıllık Getiriler</h2>
                    <hr/>
                    {data.getiri_1y === '0.00' ?
                      <div className='p-4 pt-2'>
                        <Alert variant='warning'>
                          Bu fon 1 yaşından küçük olduğu için hesaplanamıyor.
                        </Alert> 
                      </div>  : 
                      (
                        <div className='p-4 pb-0 pt-2'>
                          <Row>
                            {getiriler.map((getiri, index) => (
                              <Col className='mb-4' key={index}>
                                <small className='d-block mb-2'>{getiri.yil}</small>
                                <strong className={`d-block ${parseFloat(getiri.yillik_getiri_yuzdesi) < 0 ? 'text-danger' : 'text-success'}`}>
                                  %{getiri.yillik_getiri_yuzdesi}
                                </strong>
                              </Col>
                            ))}
                          </Row>
                          <Row>
                            <Col>
                              <Chart
                                options={chartData.options}
                                series={chartData.series}
                                type='bar'
                                width='100%'
                                height='400'
                              />
                            </Col>
                          </Row>
                        </div>
                      )
                    }
                  </div>
                </Col>
                <Col lg={12} className='mb-4'>
                  <div className='card-shadow'>
                    <h2 className='h6 m-0 px-4 pt-4 pb-2'>Aylık Getiriler</h2>
                    <hr/>
                    <div className='p-4 pt-1 pb-0'>
                      <Tabs
                        defaultActiveKey="2024"
                        id="uncontrolled-tab-example"
                        className="mb-3"
                      >
                        {getiriler.map((getiri, index) => (
                          <Tab key={index} eventKey={getiri.yil} title={getiri.yil}>
                            <Row>
                              {getiri.aylik.map((ay, index) => (
                                <Col className='mb-4' key={index}>
                                  <small className='d-block mb-2'>{ayVer(ay.ay)}</small>
                                  <strong className={`d-block ${parseFloat(ay.aylik_getiri_yuzdesi) < 0 ? 'text-danger' : 'text-success'}`}>
                                    %{ay.aylik_getiri_yuzdesi}
                                  </strong>
                                </Col>
                              ))}
                            </Row>
                          </Tab>
                        ))}
                      </Tabs>
                    </div>
                  </div>
                </Col>
              </Row>

              {data.getiri_1y === '0.00' ?
                <Row>
                  <Col lg={12} className='mb-2'>
                    <Alert variant='warning'>
                      Bu fon 1 yaşından küçük olduğu için risk parametreleri hesaplanamıyor.
                    </Alert> 
                  </Col>
                </Row>
                : 
                (
                  <Row>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>RİSK</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>{data.risk}</strong>
                          <small className='opacity-50'>Fonun risk değeri.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>STANDART SAPMA</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>{data.standart_sapma}</strong>
                          <small className='opacity-50'>Düşük olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>SHARPE ORANI</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>{data.sharpe}</strong>
                          <small className='opacity-50'>Yüksek olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>DEĞİŞKENLİK KATSAYISI</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>{data.degiskenlikKatsayisi}</strong>
                          <small className='opacity-50'>Düşük olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>AŞAĞI YÖNLÜ RİSK</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>%{data.asagiYonluRisk}</strong>
                          <small className='opacity-50'>Düşük olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>NEGATİF GETİRİLİ GÜN SAYISI</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>{data.negatifGetiriliGunSayisi}</strong>
                          <small className='opacity-50'>Düşük olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>NEGATİF GETİRİLİ GÜN ORANI</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>%{data.negatifGetiriGunOrani}</strong>
                          <small className='opacity-50'>Düşük olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} lg={6} className='mb-4'>
                      <div className='position-relative'>
                        <div className='bg-secondary rounded text-white d-flex align-items-center justify-content-center pt-2 pb-3'>
                          <h2 className='h6 text-white m-0'>MAKSİMUM KAYIP</h2>
                        </div>
                        <div className='card-shadow p-3 pb-2 z-1 mt-n-10 text-center'>
                          <strong className='h3 d-block'>%{data.maksimumKayip}</strong>
                          <small className='opacity-50'>Düşük olması tercih edilir.</small>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )
              }

              <FiyatGrafik fiyatlar={fiyatlar} fonID={data.seflink}/>                  

              <Row className='mb-4'>
                <Col>
                  <div className='card-shadow'>
                    <div className='px-4 pt-4 pb-2 d-flex align-items-center justify-content-between'>
                      <h2 className='h6 m-0'>Yatırımcı Sayısı Grafiği</h2>
                      <small>Son 3 Ay</small>
                    </div>
                    <hr/>
                    <YatirimciSayisiGrafik yatirimciSayisi={yatirimciSayisi}/>
                  </div>
                </Col>
              </Row>

              <Row className='mb-4'>
                <Col>
                  <div className='card-shadow'>
                    <div className='px-4 pt-4 pb-2 d-flex align-items-center justify-content-between'>
                      <h2 className='h6 m-0'>Kayıp Kazanç Grafiği</h2>
                      <small>Son 1 Yıl</small>
                    </div>
                    <hr/>
                    <KayipKazancGrafik kayipKazanc={kayipKazanc}/>
                  </div>
                </Col>
              </Row>

              <Row className='mb-4'>
                <Col>
                  <div className='card-shadow'>
                    <div className='px-4 pt-4 pb-2 d-flex align-items-center justify-content-between'>
                      <h2 className='h6 m-0'>Portföy Dağılım Raporu</h2>
                    </div>
                    <hr/>
                    <div className='p-4 pt-1 pb-0'>
                      <Tabs
                        defaultActiveKey="2024"
                        id="portfoy-dagilim-tab"
                        className="mb-3"
                      >
                        {portfoyDagilim.map((portfoy, index) => (
                          <Tab key={index} eventKey={portfoy.yil} title={portfoy.yil}>
                            <Row>
                              {portfoy.donemler.map((donem, index) => (
                                <Col xl={2} lg={4} md={4} sm={6} className='mb-4' key={index}>
                                  <Link prefetch={false} href={`https://www.kap.org.tr/tr/ek-indir/${donem.dosya}`} title={`${data.kod} fonu ${portfoy.yil} ${donem.donem} portföy dağılım raporu`} className='d-flex align-items-center justify-content-center py-3 border-dashed rounded bg-light text-color text-decoration-none'>
                                    <Icon.File size={28} className='me-2 opacity-25'/>
                                    <div>
                                      <small className='d-block'>{portfoy.yil} {donem.donem}</small>
                                      <small className='d-block font-xsmall opacity-50'>{donem.tarih}</small>
                                    </div>
                                  </Link>
                                </Col>
                              ))}
                            </Row>
                          </Tab>
                        ))}
                      </Tabs>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xl={5} className='mb-4'>
                  <div className='card-shadow'>
                    <div className='px-4 pt-4 pb-2 d-flex align-items-center justify-content-between'>
                      <h2 className='h6 m-0'>Varlık Dağılımı</h2>
                    </div>
                    <hr/>
                    <div className='p-4 pt-1'>
                      <Chart options={portfoyDetayOptions} series={portfoyDetaySeries} type="donut" width="100%" />
                    </div>
                  </div>
                </Col>
                <Col xl={7} className='mb-4'>
                  <div className='card-shadow'>
                    <div className='px-4 pt-4 pb-2 d-flex align-items-center justify-content-between'>
                      <h2 className='h6 m-0'>Getiri Karşılaştırma</h2>
                      <span>Son 1 Yıl</span>
                    </div>
                    <hr/>
                    <div className='p-4 pt-1'>
                      <Chart
                        options={getiriKarsilastirmaChartData.options}
                        series={getiriKarsilastirmaChartData.series}
                        type='bar'
                        width='100%'
                        height='364'
                      />
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={12} className='mb-4'>
                  <div className='card-shadow'>
                    <div className='px-4 pt-4 pb-2 d-flex align-items-center justify-content-between'>
                      <h2 className='h6 m-0'>{data.kod} Fonu Analizi</h2>
                    </div>
                    <hr/>
                    <div className='p-4 pt-1'>
                      <p>{data.dinle}</p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
          <Footer />
        </main>
      </div>
    </>
  );
}

export async function getServerSideProps(context) {
  const { params } = context;

  // Veriyi çeken fonksiyon
  async function fetchData() {
    try {
      const response = await fetch(`https://api.yatirimnedir.com/fon-analiz.php?fonID=${params.slug}`);
      const jsonData = await response.json();
      return jsonData;
    } catch (error) {
      console.error('Fetching data failed', error);
      // Veri çekme başarısız olursa, varsayılan değerlerle dön
      return {
        yillik: [],
        fiyatlar: [],
        yatirimciSayisi: 0,
        kayipKazanc: [],
        portfoyDagilim: [],
        portfoyDetay: []
      };
    }
  }

  const [data] = await Promise.all([fetchData()]);

  return {
    props: {
      data,
      getiriler: data.yillik || [],
      getiriKarsilastirma: data.getiriKarsilastirma || [],
      fiyatlar: data.fiyatlar || [],
      yatirimciSayisi: data.yatirimciSayisi || 0,
      kayipKazanc: data.kayipKazanc || [],
      portfoyDagilim: data.portfoyDagilim || [],
      portfoyDetay: data.portfoyDetay || []
    },
  };
}
